import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiCallingV2Service, API_CONFIG } from '@zayan-erp/shared';
import { ModuleRole, User, UserModuleMetadata, UserRole } from '../models/admin-console.models';

export interface AlternativeOtpEmail {
  id: string;
  email: string;
}

export interface UserListResponse {
  users: User[];
  totalCount: number;
}

export interface UserListQuery {
  /** Backend quirk: `true` returns the deactivated members, `false` the active ones. */
  isActive: boolean;
  pageNumber: number;
  pageSize: number;
  searchText?: string;
  /** Omitted for everyone; internal means a trusted email domain or an owner-set exception. */
  userType?: 'internal' | 'external';
}

/** A module with its assignable roles, as returned by the modules endpoint. */
export interface ModuleWithRoles {
  moduleId: string;
  moduleName: string;
  metadata: UserModuleMetadata;
  roles: UserRole[];
}

/** The wire shape: `metadata` is a JSON string. */
type ModuleWithRolesResponse = Omit<ModuleWithRoles, 'metadata'> & { metadata: string };

export type CreateUserRequest = Omit<
  User,
  | 'id'
  | 'role'
  | 'roleDisplayName'
  | 'dateJoined'
  | 'addedBy'
  | 'isLocked'
  | 'lockoutEnd'
  | 'isActive'
  | 'userModuleRoles'
  | 'companyName'
> & {
  password: string;
  companyName?: string;
  moduleRoles: ModuleRole[];
  /** Set when the admin has confirmed restoring a soft-deleted user with this email. */
  confirmRestore?: boolean;
  /** Only consulted when confirmRestore is true. Defaults to true on the backend if omitted. */
  sendWelcomeEmail?: boolean;
};

export type UpdateUserRequest = Omit<CreateUserRequest, 'password'>;

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  #http = inject(ApiCallingV2Service);
  #apiConfig = inject(API_CONFIG);
  baseUrl = this.#apiConfig.baseUrlV2;

  /** `isActive: true` returns the inactive members, `false` the active ones. */
  getUsers(query: UserListQuery): Observable<UserListResponse> {
    const search = query.searchText ? `&searchText=${encodeURIComponent(query.searchText)}` : '';
    const userType = query.userType ? `&userType=${query.userType}` : '';
    const url = `${this.baseUrl}/users?isActive=${query.isActive}&PageNumber=${query.pageNumber}&PageSize=${query.pageSize}${search}${userType}`;

    return this.#http
      .getData<UserListResponse>(url, {
        showLoader: false,
        showMessage: false
      })
      .pipe(
        map((response: UserListResponse | null) => ({
          users: response?.users ?? [],
          totalCount: response?.totalCount ?? 0
        }))
      );
  }

  /** `metadata` arrives as a JSON string and is parsed here so callers get an object. */
  getModules(): Observable<ModuleWithRoles[]> {
    return this.#http
      .getData<ModuleWithRolesResponse[]>(`${this.baseUrl}/modules`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(
        map((modules: ModuleWithRolesResponse[] | null) =>
          (modules ?? []).map(module => ({ ...module, metadata: JSON.parse(module.metadata) }))
        )
      );
  }

  canManageSystemOwner(): Observable<boolean> {
    return this.#http
      .getData<{ canManageSystemOwner: boolean }>(`${this.baseUrl}/users/system-owner/can-manage`, {
        showLoader: false,
        showMessage: false
      })
      .pipe(map(response => response?.canManageSystemOwner ?? false));
  }

  createUser(request: CreateUserRequest): Observable<void> {
    return this.#http.postData<void>(
      `${this.baseUrl}/users`,
      { showLoader: false, showMessage: true, showError: false, message: 'Member added successfully' },
      request
    );
  }

  updateUser(userId: string, request: UpdateUserRequest): Observable<void> {
    return this.#http.putData<void>(`${this.baseUrl}/users/${userId}`, request, {
      showLoader: false,
      showMessage: true,
      message: 'Member updated successfully'
    });
  }

  resetPassword(userId: string, password: string, confirmPassword: string): Observable<void> {
    return this.#http.putData<void>(
      `${this.baseUrl}/users/${userId}/reset-password`,
      { password, confirmPassword },
      { showLoader: false, showMessage: true, message: 'Password reset successfully' }
    );
  }

  deleteUser(userId: string): Observable<void> {
    return this.#http.deleteData<void>(`${this.baseUrl}/users/${userId}`, {
      showLoader: false,
      showMessage: true,
      message: 'Member deleted successfully'
    });
  }

  /** Flips the member between active and inactive (IsDeleted). */
  toggleUserStatus(userId: string): Observable<void> {
    return this.#http.patchData<void>(`${this.baseUrl}/users/${userId}/toggle-status`, null, {
      showLoader: false,
      showMessage: true,
      message: 'User status updated successfully'
    });
  }

  /** Restores a soft-deleted member via the same toggle-status endpoint. */
  restoreUser(userId: string): Observable<void> {
    return this.#http.patchData<void>(`${this.baseUrl}/users/${userId}/toggle-status`, null, {
      showLoader: false,
      showMessage: true,
      message: 'Member restored successfully'
    });
  }

  /** System Owner only: counts the member as internal even without a trusted email domain. */
  setTreatAsInternal(userId: string, treatAsInternal: boolean): Observable<void> {
    return this.#http.putData<void>(
      `${this.baseUrl}/users/${userId}/treat-as-internal`,
      { treatAsInternal },
      {
        showLoader: false,
        showMessage: true,
        message: treatAsInternal ? 'Member will be treated as internal' : 'Internal exception removed'
      }
    );
  }

  unlockUser(userId: string): Observable<void> {
    return this.#http.patchData<void>(`${this.baseUrl}/users/${userId}/unlock-member`, null, {
      showLoader: false,
      showMessage: true,
      message: 'Member unlocked successfully'
    });
  }

  getAlternativeOtpEmails(userId: string): Observable<AlternativeOtpEmail[]> {
    return this.#http.getData<AlternativeOtpEmail[]>(`${this.baseUrl}/users/${userId}/alternative-otp-emails`, {
      showLoader: false,
      showMessage: false
    });
  }

  addAlternativeOtpEmails(userId: string, emails: string[]): Observable<unknown> {
    return this.#http.postData(
      `${this.baseUrl}/users/${userId}/alternative-otp-emails`,
      { showLoader: false, showMessage: true, message: 'Alternative OTP emails added successfully' },
      { emails }
    );
  }

  updateAlternativeOtpEmail(userId: string, alternativeEmailId: string, email: string): Observable<unknown> {
    return this.#http.putData(
      `${this.baseUrl}/users/${userId}/alternative-otp-emails/${alternativeEmailId}`,
      { email },
      { showLoader: true, showMessage: true, message: 'Alternative OTP email updated successfully' }
    );
  }

  deleteAlternativeOtpEmail(userId: string, alternativeEmailId: string): Observable<unknown> {
    return this.#http.deleteData(`${this.baseUrl}/users/${userId}/alternative-otp-emails/${alternativeEmailId}`, {
      showLoader: false,
      showMessage: true,
      message: 'Alternative OTP email deleted successfully'
    });
  }
}
